import { Callout, Typography, YStack } from '@shinetools/lumen-react'
import { useTranslation } from 'react-i18next'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import type { Contact } from '@features/contacts/types/contact'
import { useTaxRates } from '@modules-deprecated/app/taxrates/taxRatesContext'
import { getAccountFromContact } from '@views/bills/elements/BillEditableForm/utils/getAccountFromContact'
import { getTaxRateFromContact } from '@views/bills/elements/BillEditableForm/utils/getTaxRateFromContact'
import { AutomationUpgradeSection } from '@views/suppliers/elements/AutomationUpgradeSection'
import { getAccountLabel } from '@views/suppliers/utils/getAccountLabel'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { isAuthorized } from '../../../../../../../../utils/isAuthorized'
import { LabelValueRow } from '../LabelValueRow'

interface AutomationRuleProps {
  contact: Contact
}

export const AutomationRule = ({ contact }: AutomationRuleProps) => {
  const { t } = useTranslation()
  const { taxRates = [] } = useTaxRates()
  const isAutomationAuthorized = isAuthorized(Scope.ContactBookkeepingDefaults)
  const isDanishContact = contact.countryId === 'DK'
  const { accounts } = useFetchAccounts({ enabled: isAutomationAuthorized && isDanishContact })
  const taxRate = getTaxRateFromContact(taxRates, contact)
  const expenseAccount = getAccountFromContact(accounts, contact)
  const expenseAccountLabel = expenseAccount ? getAccountLabel(expenseAccount) : undefined

  if (!isAutomationAuthorized) {
    return (
      <YStack paddingHorizontal={24}>
        <AutomationUpgradeSection trackingContext={TrackingContext.SupplierAutomationRule} />
      </YStack>
    )
  }

  return (
    <YStack gap={12}>
      <Typography.Text size="large" weight="semibold">
        {t('suppliers.detail.automation.title')}
      </Typography.Text>
      <Callout color="informative" message={t('suppliers.detail.automation.callout.hint')} />
      <YStack>
        <LabelValueRow label={t('description')} value={contact?.defaultExpenseProductDescription} />
        {isDanishContact ? (
          <LabelValueRow label={t('suppliers.detail.form.default_purchase_category')} value={expenseAccountLabel} />
        ) : null}
        <LabelValueRow label={t('suppliers.detail.automation.label.tax_rate')} value={taxRate?.name} />
      </YStack>
    </YStack>
  )
}
